/**
 * Service for managing COC Assistant API operations
 * Implements endpoints from the COC Assistant API Frontend Integration Guide
 * 
 * The COC Assistant interprets COC documents (handwritten or digital) and converts
 * them into structured JSON for order creation.
 */

import { makeAuthenticatedRequest } from './backendApiService';

const COC_ASSISTANT_BASE_URL = '/api/v1/coc-assistant';

/**
 * COC Assistant Service Class
 */
class CocAssistantService {
    /**
     * Check if the COC Assistant service is running
     * @returns {Promise<Object>} Health status object
     */
    async healthCheck() {
        try {
            // Health check doesn't require authentication
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}${COC_ASSISTANT_BASE_URL}/health`);
            if (!response.ok) {
                throw new Error(`Health check failed: ${response.status}`);
            }
            return await response.json();
        } catch (error) {
            console.error('Failed to check COC Assistant health:', error);
            throw error;
        }
    }

    /**
     * Interpret a COC document and extract structured information
     * @param {string} accessToken - JWT access token
     * @param {Object} interpretRequest - Interpretation request
     * @param {string} interpretRequest.cocText - Raw text from COC
     * @param {string} [interpretRequest.sessionId] - Optional session ID for context
     * @param {Object} [interpretRequest.clarifications] - Optional field clarifications
     * @returns {Promise<Object>} Interpretation response with extracted data
     */
    async interpretCoc(accessToken, interpretRequest) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${COC_ASSISTANT_BASE_URL}/interpret`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(interpretRequest)
                }
            );
            return response;
        } catch (error) {
            console.error('Failed to interpret COC:', error);
            throw error;
        }
    }

    /**
     * Interpret a COC document from a PDF file
     * @param {string} accessToken - JWT access token
     * @param {File} file - PDF file containing the COC
     * @param {string} [sessionId] - Optional session ID for context
     * @returns {Promise<Object>} Interpretation response with extracted data
     */
    async interpretCocFromPdf(accessToken, file, sessionId) {
        try {
            // Validate file
            if (!file) {
                throw new Error('No file provided');
            }

            // Validate file type
            if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
                throw new Error('File must be a PDF');
            }

            // Log file details for debugging
            console.log('Uploading PDF:', {
                name: file.name,
                type: file.type,
                size: file.size,
                sessionId
            });

            const formData = new FormData();
            formData.append('file', file, file.name); // Include filename
            
            if (sessionId) {
                formData.append('sessionId', sessionId);
            }

            // For multipart/form-data, we need to use fetch directly
            // and let the browser set the Content-Type header with boundary
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}${COC_ASSISTANT_BASE_URL}/interpret-pdf`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                        // Do NOT set Content-Type - browser will set it with boundary
                    },
                    body: formData
                }
            );

            // Try to get detailed error message
            if (!response.ok) {
                let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                try {
                    const errorData = await response.json();
                    console.error('Backend error response:', errorData);
                    errorMessage = errorData.error || errorData.message || errorMessage;
                    
                    // Include additional error details if available
                    if (errorData.details) {
                        errorMessage += ` - ${errorData.details}`;
                    }
                    if (errorData.timestamp) {
                        errorMessage += ` (at ${errorData.timestamp})`;
                    }
                } catch (parseError) {
                    // If response is not JSON, try to get text
                    try {
                        const errorText = await response.text();
                        if (errorText) {
                            console.error('Backend error text:', errorText);
                            errorMessage = errorText.substring(0, 200); // Limit length
                        }
                    } catch (textError) {
                        console.error('Could not parse error response');
                    }
                }
                throw new Error(errorMessage);
            }

            const result = await response.json();
            console.log('PDF interpretation successful:', {
                confidence: result.overallConfidence,
                hasInterpretation: !!result.interpretation,
                uncertainFieldsCount: result.uncertainFields?.length || 0
            });

            return result;
        } catch (error) {
            console.error('Failed to interpret COC from PDF:', error);
            throw error;
        }
    }

    /**
     * Provide clarifications for uncertain fields from a previous interpretation
     * @param {string} accessToken - JWT access token
     * @param {Object} clarificationRequest - Clarification request
     * @param {string} clarificationRequest.confirmationId - UUID from previous response
     * @param {Object} clarificationRequest.clarifications - Field path -> corrected value mappings
     * @returns {Promise<Object>} Updated interpretation response
     */
    async provideClarification(accessToken, clarificationRequest) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${COC_ASSISTANT_BASE_URL}/clarify`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(clarificationRequest)
                }
            );
            return response;
        } catch (error) {
            console.error('Failed to provide clarification:', error);
            throw error;
        }
    }

    /**
     * Retrieve interpretation history for a session
     * @param {string} accessToken - JWT access token
     * @param {string} sessionId - Session identifier
     * @returns {Promise<Object>} History response
     */
    async getHistory(accessToken, sessionId) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${COC_ASSISTANT_BASE_URL}/history/${sessionId}`
            );
            return response;
        } catch (error) {
            console.error('Failed to get COC interpretation history:', error);
            throw error;
        }
    }

    /**
     * Clear interpretation history for a session
     * @param {string} accessToken - JWT access token
     * @param {string} sessionId - Session identifier
     * @returns {Promise<Object>} Clear history response
     */
    async clearHistory(accessToken, sessionId) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${COC_ASSISTANT_BASE_URL}/history/${sessionId}`,
                {
                    method: 'DELETE'
                }
            );
            return response;
        } catch (error) {
            console.error('Failed to clear COC interpretation history:', error);
            throw error;
        }
    }

    /**
     * Submit a COC PDF for async processing
     * @param {string} accessToken - JWT access token
     * @param {File} file - PDF file containing the COC
     * @param {string} [submittedBy] - Optional user identifier
     * @returns {Promise<Object>} Job submission response with jobId
     */
    async submitCocPdf(accessToken, file, submittedBy) {
        try {
            // Validate file
            if (!file) {
                throw new Error('No file provided');
            }

            // Validate file type
            if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
                throw new Error('File must be a PDF');
            }

            console.log('Submitting COC PDF for async processing:', {
                name: file.name,
                type: file.type,
                size: file.size,
                submittedBy
            });

            const formData = new FormData();
            formData.append('file', file, file.name);
            
            if (submittedBy) {
                formData.append('submittedBy', submittedBy);
            }

            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}${COC_ASSISTANT_BASE_URL}/submit`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    },
                    body: formData
                }
            );

            if (!response.ok) {
                let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                try {
                    const errorData = await response.json();
                    console.error('Backend error response:', errorData);
                    errorMessage = errorData.error || errorData.message || errorMessage;
                } catch (parseError) {
                    console.error('Could not parse error response');
                }
                throw new Error(errorMessage);
            }

            const result = await response.json();
            console.log('COC PDF submitted successfully:', {
                jobId: result.jobId,
                fileName: result.fileName
            });

            return result;
        } catch (error) {
            console.error('Failed to submit COC PDF:', error);
            throw error;
        }
    }

    /**
     * Submit COC text for async processing
     * @param {string} accessToken - JWT access token
     * @param {string} cocText - COC text content
     * @param {string} [submittedBy] - Optional user identifier
     * @returns {Promise<Object>} Job submission response with jobId
     */
    async submitCocText(accessToken, cocText, submittedBy) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${COC_ASSISTANT_BASE_URL}/submit-text`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        cocText,
                        submittedBy
                    })
                }
            );
            return response;
        } catch (error) {
            console.error('Failed to submit COC text:', error);
            throw error;
        }
    }

    /**
     * Get the status of a processing job
     * @param {string} accessToken - JWT access token
     * @param {string} jobId - Job identifier
     * @returns {Promise<Object>} Job status response
     */
    async getJobStatus(accessToken, jobId) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${COC_ASSISTANT_BASE_URL}/status/${jobId}`
            );
            return response;
        } catch (error) {
            console.error('Failed to get job status:', error);
            throw error;
        }
    }

    /**
     * Get the result of a completed job
     * @param {string} accessToken - JWT access token
     * @param {string} jobId - Job identifier
     * @returns {Promise<Object>} Job result with interpretation data
     */
    async getJobResult(accessToken, jobId) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${COC_ASSISTANT_BASE_URL}/result/${jobId}`
            );
            return response;
        } catch (error) {
            console.error('Failed to get job result:', error);
            throw error;
        }
    }

    /**
     * Cancel a pending or processing job
     * @param {string} accessToken - JWT access token
     * @param {string} jobId - Job identifier
     * @returns {Promise<Object>} Cancellation response
     */
    async cancelJob(accessToken, jobId) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${COC_ASSISTANT_BASE_URL}/cancel/${jobId}`,
                {
                    method: 'DELETE'
                }
            );
            return response;
        } catch (error) {
            console.error('Failed to cancel job:', error);
            throw error;
        }
    }
}

// Create singleton instance
const cocAssistantService = new CocAssistantService();

export default cocAssistantService;
